import { eventsApi, type TimelineEventDto } from '../../services/api';
import { onLocaleChange, t } from '../../services/i18n';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { openTimelineAddDialog } from './timeline-dialogs';
import { esc, filterEvents } from './timeline-shared';
import './rc-timeline-events-view';
import { RcTimelineEventsView } from './rc-timeline-events-view';

const TIMELINE_EVENTS = ['add', 'delete'] as const;

export class RcTimelineEvents extends HTMLElement {
  private tenantId = '';
  private tenantName = '';
  private rawEvents: TimelineEventDto[] = [];
  private events: TimelineEventDto[] = [];
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    TIMELINE_EVENTS.forEach((eventName) => this.addEventListener(`timeline-${eventName}`, this.handleTimelineEvent as EventListener));
    this.tenantId = this.getAttribute('tenant-id') || '';
    this.tenantName = this.getAttribute('tenant-name') || '';
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    if (this.tenantId) {
      void this.loadEvents();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    TIMELINE_EVENTS.forEach((eventName) => this.removeEventListener(`timeline-${eventName}`, this.handleTimelineEvent as EventListener));
  }

  applyFilter(): void {
    this.events = filterEvents(this.rawEvents, this.getAttribute('start-date') || '', this.getAttribute('end-date') || '');
    this.render();
  }

  async loadEvents(): Promise<void> {
    try {
      this.rawEvents = await eventsApi.getByTenant(this.tenantId);
      this.events = filterEvents(this.rawEvents, this.getAttribute('start-date') || '', this.getAttribute('end-date') || '');
      this.render();
    } catch (error) {
      console.error('Failed to load events:', error);
      this.innerHTML = `<div class="empty-state">${t('timeline.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-timeline-events-view></rc-timeline-events-view>';
    const view = this.querySelector('rc-timeline-events-view') as RcTimelineEventsView | null;
    if (!view) return;
    view.events = this.events;
    view.render();
  }

  private handleTimelineEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'timeline-add':
        openTimelineAddDialog(this.tenantId, this.tenantName, async () => this.loadEvents());
        return;
      case 'timeline-delete':
        if (detail.id) await this.handleDelete(detail.id);
        return;
      default:
        return;
    }
  };

  private async handleDelete(id: string): Promise<void> {
    if (!await confirmDialog(t('timeline.confirmDelete'))) return;
    try {
      await eventsApi.delete(id);
      await this.loadEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
  }
}

if (!customElements.get('rc-timeline-events')) {
  customElements.define('rc-timeline-events', RcTimelineEvents);
}
